'use client';

import { Dialog, DialogContent, DialogTitle } from '@shared/components/shadcn';
import { useFullStoryLoginFailure } from '@shared/tracking';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { GenericErrorStep } from '../../GenericErrorStep/GenericErrorStep';
import type { RequiredActionProps } from '../types';

function AccountClosedAlertDialog({ open, onOpenChange, playerId }: RequiredActionProps) {
  const t = useTranslations('errors.account_closed');

  useFullStoryLoginFailure({
    errorKey: open ? 'closed' : undefined,
    playerId,
  });

  const handleHelpCentre = useCallback(() => {
    window.open(ROUTES.HELP_CENTRE.BASE_URL, '_blank');
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="account-closed-dialog"
        data-track-feature="Log In"
        data-track-container="Failure Confirmation"
        data-track-title={t('title')}
        data-track-reason={t('description')}
        showCloseButton={true}
        className="w-full max-w-md h-full md:h-auto max-h-md"
        onEscapeKeyDown={e => e.preventDefault()}
        onPointerDownOutside={e => e.preventDefault()}
        onInteractOutside={e => e.preventDefault()}
        // aria-describedby is optional for accessibility, but Radix UI will warn unless we explicitly opt-out.
        // @see https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/#:~:text=Optionally%2C%20the%20aria%2Ddescribedby%20property
        // @see https://www.radix-ui.com/primitives/docs/components/dialog#description
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">{t('title')}</DialogTitle>
        <GenericErrorStep onAction={handleHelpCentre} errorTitle={t('title')} errorMessage={t('description')} actionText={t('button_help_centre')} />
      </DialogContent>
    </Dialog>
  );
}

export { AccountClosedAlertDialog };
